/**
 * Pure mappings for the touch control scheme. Kept out of App/HUD so the curves
 * are testable and tunable in one place — on a phone these numbers ARE the feel
 * of the game, and they're impossible to reason about buried in an event handler.
 */

/** Gain floor, as a fraction of the full aim sensitivity. */
export const FINE_AIM = 0.28;
/** Drag speed (px/ms) at which the aim gain reaches full. */
export const FAST_PX_MS = 1.6;

/**
 * Speed-adaptive aim gain.
 *
 * A finger is blunt and it covers the very ball you're lining up on, so a fixed
 * gain forces a choice between "can't swing the cue all the way round" and
 * "can't nudge it a fraction of a degree". Scaling with drag SPEED gives both: a
 * fast sweep travels far, a slow deliberate drag fines down to FINE_AIM.
 *
 * @param dx  pointer movement since the last event, px (sign ignored)
 * @param dtMs  time since the last move event; clamped to ≥1 so a burst of
 *              same-timestamp events can't divide by zero into an infinite gain
 * @param base  the full-speed gain (rad per px)
 */
export function aimGain(dx: number, dtMs: number, base: number): number {
  const speed = Math.abs(dx) / Math.max(1, dtMs);
  const k = Math.min(1, speed / FAST_PX_MS);
  return base * (FINE_AIM + (1 - FINE_AIM) * k);
}

/**
 * Absolute position on the vertical tilt dial → cue elevation in degrees.
 * The TOP of the track is the steepest cue, so the fraction is inverted; a
 * zero-height track (not yet laid out) returns null rather than NaN.
 */
export function tiltFromTrack(clientY: number, top: number, height: number, maxDeg: number): number | null {
  if (!(height > 0)) return null;
  const frac = 1 - (clientY - top) / height;
  return Math.max(0, Math.min(1, frac)) * maxDeg;
}
